<template>
  <td class="sticky-column">
    <div>{{ petData.name }}</div>
  </td>
  <td><div>{{ petData.species }}</div></td>
  <td><div>{{ petData.breed }}</div></td>
  <td><div>{{ petData.gender }}</div></td>
  <td><div>{{ petData.birth }}</div></td>
  <td>
    <span v-if="ageYears == 1">{{ ageYears }} {{ $t('General.Year') }}</span>
    <span v-if="ageYears > 1">{{ ageYears }} {{ $t('General.Years') }}</span>
    <span v-if="ageMonths == 1">{{ ageMonths }} {{ $t('General.Month') }}</span>
    <span v-if="ageMonths > 1">{{ ageMonths }} {{ $t('General.Months') }}</span>
    <span v-if="ageYears == 0 && ageMonths == 0 && ageDays == 1">
      {{ ageDays }} {{ $t('General.Day') }}
    </span>
    <span v-if="ageYears == 0 && ageMonths == 0 && ageDays > 1">
      {{ ageDays }} {{ $t('General.Days') }}
    </span>
  </td>
  <td><div>{{ petData.fertile_status }}</div></td>
  <td><div>{{ petData.weight }}</div></td>
  <td v-if="considerInsurance">
    <div>{{ petData.policy }}</div>
  </td>
  <td v-if="getAccessType == 'manager'"
    :class="{ 'inactive-status': petData.status === $t('PetData.TK_Inactive') }">
    <div>{{ petData.status }}</div>
  </td>
  <td 
    v-if="getAccessType == 'manager'"
    :class="{ 'inactive-status': petData.status === $t('PetData.TK_Inactive') }">
    <div>{{ petData.status_date }}</div>
  </td>
  <td>
    <div class="is-flex is-justify-content-start" style="gap: 1em;">
      <div>
        <button
          type="button"
          v-if="!getEditedCustEditingPet &&
                getEditingCustomer &&
                !getShowingPetRecord &&
                (getAccessType == 'manager'  || petData.pet_id == 0)"
          class="button is-warning is-small"
          @click="iniEditing(petData)"
          :disabled="petData.status === $t('PetData.TK_Inactive')"
        >
          {{ $t('General.Edit') }}
        </button>
      </div>
      <div v-if="!getEditedCustEditingPet &&
                 getEditingCustomer &&
                 !getShowingPetRecord &&
                 (getAccessType == 'manager'  || petData.pet_id == 0) &&
                 petData.status !== $t('PetData.TK_Inactive')">
        <button
          type="button"
          class="button is-danger is-small"
          @click="remove(petData, $t)"
        >
          {{ (getAccessType === 'manager' && petData.pet_id != 0)
              ? $t('General.Deactivate')
              : $t('General.Delete') }}
        </button>
      </div>
      <div v-if="!getEditedCustEditingPet &&
                 getEditingCustomer &&
                 !getShowingPetRecord &&
                 getAccessType == 'manager' &&
                 petData.status !== $t('PetData.TK_Active')">
        <button
          type="button"
          class="button is-warning is-small"
          @click="reactivate(petData, $t)"
        >
          {{ $t('General.Reactivate') }}
        </button>
      </div>
      <div v-if="!getEditingCustomer && !getShowingPetRecord">
        <button
          type="button"
          class="button is-warning is-small"
          @click="iniRecord(petData)"
        >
          {{ $t('General.Record') }}
        </button>
      </div>
    </div>
  </td>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { mapGetters, mapActions } from "vuex";
import { Notyf } from "notyf";
import { differenceInYears, differenceInMonths, differenceInDays } from "date-fns";

const notyf = new Notyf()

export default defineComponent({
  name: "CustDataPetTableRow",

  props: {
    petData: { type: Object, required: true },
    language: { type: String, required: true },
  },

  data() {
    return {
      ageYears: 0,
      ageMonths: 0,
      ageDays: 0,
    };
  },

  computed:
  {
    ...mapGetters([ "getAccessType", "getEditedCustEditingPet", "getShowingPetRecord", "getEditingCustomer", ]),
    
    considerInsurance(): boolean
    {
      return process.env.VUE_APP_CONSIDER_INSURANCE === "true";
    },
  },

  methods:
  {
    ...mapActions([ "iniEditedCustPetEditing", "toggleEditedCustPetEditing", "iniPetRecord", "toggleShowingPetRecord", 
                    "savePetToBeDeleted", "removePetEditedCustomer", "savePetToBeReactivated", "reactivatePetEditedCustomer"]),

    async iniEditing(editingPetData: any)
    {
      await this.iniEditedCustPetEditing(editingPetData);
      await this.toggleEditedCustPetEditing(false);
    },

    async iniRecord(showRecordPetData: any)
    {
      if (showRecordPetData.pet_id != 0)
      {
        await this.iniPetRecord({ editingPetData: showRecordPetData, language: this.language });
        await this.toggleShowingPetRecord(false);
      }
      else
      {
        console.warn("Error: Pet ID is 0");
      }
    },

    async remove(petData: any, $t: any)
    {
      if (confirm($t && $t("PetData.ConfirmDelete")))
      {
        if (petData.pet_id != 0)
        {
          await this.savePetToBeDeleted(petData.pet_id);
        }
        try 
        {
          await this.removePetEditedCustomer({pet_pos: petData.pet_pos, pet_id: petData.pet_id, status: $t('PetData.TK_Inactive')});
          notyf.success($t("PetData.NotyfPetRemoved"));
        }
        catch (e)
        {
          notyf.error($t("PetData.NotyfErrorRemovePet"));
        }
      }
    },
    async reactivate(petData: any, $t: any)
    {
      if (confirm($t && $t("PetData.ConfirmReactivate")))
      {
        if (petData.pet_id != 0)
        {
          await this.savePetToBeReactivated(petData.pet_id);
        }
        try 
        {
          await this.reactivatePetEditedCustomer({pet_pos: petData.pet_pos, status: $t('PetData.TK_Active')});
          notyf.success($t("PetData.NotyfPetReactivated"));
        }
        catch (e)
        {
          notyf.error($t("PetData.NotyfErrorReactivatePet"));
        }
      }
    },
    
  },

  beforeMount()
  {
    const birthDate = new Date(this.petData.birth);
    this.ageYears = differenceInYears(new Date(), birthDate);
    this.ageMonths = differenceInMonths(new Date(), birthDate) - 12 * this.ageYears;
    this.ageDays = differenceInDays(new Date(), birthDate);
  }
});
</script>

<style scoped>
.pointer {
  cursor: pointer;
}
.sticky-column {
  position: sticky;
  max-width: 150px;
  left: 0;
  background: #fff;
  z-index: 2;
  padding-left: 5px;
  border-right: 1px solid #ddd;
  border-left: 1px solid #ddd;
  will-change: left;
}
.inactive-status {
  color: #a00; /* dark red text */
  font-weight: bold;
}
</style>
